import {Edge, Node} from 'reactflow';

import {PipelineEdge, PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import {MakeAllFieldsRequired, Override} from 'util/Types';

/**
 * A node in the analysis graph.
 *
 * Currently, every node represents a step in the pipeline.
 */
export enum AnalysisGraphNodeType {
  STEP = 'STEP',
}

/**
 * An edge in the analysis graph.
 *
 * Currently, every edge represents data frames or files flowing between steps.
 */
export enum AnalysisGraphEdgeType {
  EDGE = 'EDGE',
}

/**
 * The analysis-specific data associated with a node in the analysis flowchart graph
 */
export interface AnalysisGraphNodeData {
  step: PipelineStep;
}

/**
 * A node in the analysis flowchart graph, using the interface expected by `reactflow`.
 */
export type AnalysisGraphNode = Pick<Node<AnalysisGraphNodeData>, 'id' | 'position' | 'data' | 'type'> & {
  type: AnalysisGraphNodeType;
};

/**
 * Extend `PipelineEdge` to contain full data from parent and child steps.
 */
type PipelineEdgeWithFullData = Override<
  PipelineEdge,
  {
    fromStep: PipelineStep;
    fromStepData: PipelineStepData;
    toStep: PipelineStep;
    toStepData: PipelineStepData;
  }
>;

/**
 * The analysis-specific data associated with an edge in the analysis flowchart graph
 */
export interface AnalysisGraphEdgeData {
  edge: PipelineEdgeWithFullData;
}

/**
 * An edge in the analysis flowchart graph, using the interface expected by `reactflow`
 */
export type AnalysisGraphEdge = MakeAllFieldsRequired<
  Pick<
    Edge<AnalysisGraphEdgeData>,
    'id' | 'source' | 'target' | 'data' | 'type' | 'sourceHandle' | 'targetHandle'
  >
> & {
  type: AnalysisGraphEdgeType;
};

/**
 * The analysis flowchart graph, the data structure on which `AnalysisFlowchart` is built.
 */
export interface AnalysisGraph {
  nodes: Array<AnalysisGraphNode>;
  edges: Array<AnalysisGraphEdge>;
}
